// ink-component
import React, { HTMLAttributes } from 'react';
import styled from 'styled-components';

import { footerActionsTokens } from '../../tokens/components/FooterActions/tokens';

import { Button } from '../Button/Button';
import PrimaryAction from '../Block/BlockFooter/PrimaryAction/PrimaryAction';

import { PrimaryActionType, SecondaryActionType, TertiaryActionType } from '../Block/Block';

import useWindowWidth from '../../hooks/useWindowWidth';

import getGlobalProps from '../../helpers/constants/global-props';

export interface FooterActionsProps extends HTMLAttributes<HTMLDivElement> {
  /** Disables all action buttons while async processes are running. */
  isLoading?: boolean;
  /** Primary action configuration object. */
  primaryAction: PrimaryActionType;
  /** Secondary action configuration object. */
  secondaryAction?: SecondaryActionType;
  /** Tertiary action configuration object. */
  tertiaryAction?: TertiaryActionType;
  /** Form ID associated with primary action. */
  formId?: string;
  /** Marks the element with a given ID to make it easier to select in tests */
  'data-testid'?: string | number;
}

const FooterActionsWrapper = styled.div`
  display: flex;
  justify-content: ${({ theme }) => (theme.secondaryAction ? 'space-between' : 'right')};
  flex-wrap: ${({ theme }) => (theme.isMobile ? 'wrap-reverse' : 'nowrap')};
  gap: ${footerActionsTokens.SpacingGapBaseDefault};
  width: 100%;
`;
const FooterActionsPrimaryWrapper = styled.div`
  display: flex;
  flex-wrap: ${({ theme }) => (theme.isMobile ? 'wrap' : 'nowrap')};
  gap: ${footerActionsTokens.SpacingGapBaseDefault};
  width: ${({ theme }) => (theme.isMobile || theme.isFullPrimaryAction ? '100%' : 'auto')};
`;

const globalProps = getGlobalProps({});

/**
 * @purpose Arranges action buttons in a defined set of configurations
 * @status beta
 */
const FooterActions = ({
  className,
  style,
  isLoading,
  primaryAction,
  secondaryAction,
  tertiaryAction,
  formId,
  'data-testid': dataTestId,
  ...otherProps
}: FooterActionsProps) => {
  const { isMobile } = useWindowWidth();
  const isFullPrimaryAction = primaryAction.size === 'full';

  return (
    <FooterActionsWrapper
      theme={{ secondaryAction, isMobile }}
      {...otherProps}
      {...globalProps}
      data-testid={dataTestId}
    >
      {tertiaryAction && isMobile && (
        <Button
          type="transparent"
          size="full"
          isLoading={isLoading}
          onClick={tertiaryAction.callback}
          data-testid={tertiaryAction['data-testid']}
        >
          {tertiaryAction.label}
        </Button>
      )}
      {secondaryAction && (
        <Button
          size={isMobile ? 'full' : 'auto'}
          isLoading={isLoading}
          onClick={secondaryAction.callback}
          data-testid={secondaryAction['data-testid']}
        >
          {secondaryAction.label}
        </Button>
      )}
      <FooterActionsPrimaryWrapper theme={{ isFullPrimaryAction, isMobile }}>
        {tertiaryAction && !isMobile && (
          <Button
            type="transparent"
            size="auto"
            isLoading={isLoading}
            onClick={tertiaryAction.callback}
            data-testid={tertiaryAction['data-testid']}
          >
            {tertiaryAction.label}
          </Button>
        )}
        <PrimaryAction
          {...primaryAction}
          size={isFullPrimaryAction || isMobile ? 'full' : 'wide'}
          formId={formId}
          callback={primaryAction.callback}
          actionIsLoading={isLoading}
        />
      </FooterActionsPrimaryWrapper>
    </FooterActionsWrapper>
  );
};

FooterActions.defaultProps = {
  isLoading: false,
};

export default FooterActions;
